import React from 'react'
import ReactDOM from 'react-dom'
import { useCart } from '../contexts/CartContext'
import { useAppMode } from '../contexts/AppModeContext'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
// base items; dynamic ones (Orders/Login/Profile) added based on auth
const baseItems = [
  { label: 'Home', icon: <i className="ph ph-house" />, to: '/' },
  { label: 'Search', icon: <i className="ph ph-magnifying-glass" />, to: '/search' },
  { label: 'Cart', icon: <i className="ph ph-shopping-cart" />, to: '/cart' }
]

const BottomNavBar = () => {
  const location = useLocation()
  const { token, user } = useAuth()
  const { items } = useCart() || {}
  const { isAppMode } = useAppMode()
  const itemCount = Array.isArray(items) ? items.reduce((sum, item) => sum + (item.quantity || 1), 0) : 0
  const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 992)

  // Track viewport size to toggle desktop/mobile behavior
  React.useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // ensure page content has bottom space so nav doesn't cover it (mobile only)
  React.useEffect(() => {
    if (isDesktop) return // no bottom padding needed on desktop
    const bottomHeight = 60 // nav height (~52) + small gap
    const body = document.body
    // apply padding-bottom if less than required
    const prev = body.style.paddingBottom
    body.style.paddingBottom = `calc(${bottomHeight}px + env(safe-area-inset-bottom, 0px))`
    return () => {
      body.style.paddingBottom = prev
    }
  }, [isDesktop])
  // In site mode on desktop, don't render the bottom nav
  if (!isAppMode && isDesktop) return null
  // The web landing page has its own calls to action.
  if (!isAppMode && !(token && user) && location.pathname === '/') return null

  // build nav items
  // Signed out on the web, "/" is the landing page; Home means the shop.
  const dynamic = baseItems.map(item => (item.to === '/' && !isAppMode && !(token && user) ? { ...item, to: '/shop' } : item))
  if (token && user) {
    // Use a clearer orders icon
    dynamic.splice(1, 0, { label: 'Orders', icon: <i className="ph ph-clipboard-text" />, to: '/orders' })
    dynamic.push({ label: 'Profile', icon: <i className="ph ph-user" />, to: '/account' })
  } else {
    dynamic.push({ label: 'Login', icon: <i className="ph ph-sign-in" />, to: '/account-login' })
  }

  const navEl = (
    <nav
      className="bottom-nav-bar"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        background: '#fff',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        zIndex: 9999,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        minHeight: 52
      }}
    >
      {dynamic.map(item => {
        const isCart = item.label === 'Cart'
        const hasCustomBadge = item.badge && item.badge > 0
        return (
          <Link
            key={item.label}
            to={item.to}
            style={{
              flex: 1,
              textAlign: 'center',
              color: location.pathname === item.to ? '#299E60' : '#888',
              textDecoration: 'none',
              fontSize: '18px',
              padding: '4px 0',
              position: 'relative',
              lineHeight: 1
            }}
          >
            {item.icon}
            {isCart && itemCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: 2,
                  right: '28%',
                  background: '#f00',
                  color: '#fff',
                  borderRadius: '50%',
                  fontSize: '10px',
                  width: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2
                }}
              >
                {itemCount > 9 ? '9+' : itemCount}
              </span>
            )}
            {hasCustomBadge && (
              <span
                style={{
                  position: 'absolute',
                  top: 2,
                  right: '28%',
                  background: '#e74c3c',
                  color: '#fff',
                  borderRadius: '50%',
                  fontSize: '10px',
                  width: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2
                }}
              >
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
            <div style={{ fontSize: '11px', marginTop: 2 }}>{item.label}</div>
          </Link>
        )
      })}
    </nav>
  )

  // Portal to <body> so the fixed nav is never affected by an ancestor's
  // transform / filter / will-change, which would otherwise reparent it
  // and make it sit mid-page instead of at the viewport bottom.
  if (typeof document === 'undefined') return navEl
  return ReactDOM.createPortal(navEl, document.body)
}

export default BottomNavBar
